import * as amplitude from '@amplitude/analytics-browser';

import { webCampaign } from '@/modules/webCampaign';
import { retrieveDecimalDigitsFromUUID } from '@/utils/retrieveDecimalDigitsFromUUID';

import { AmplitudePlatformPlugin } from './PlatformPlugin';

import type { AmplitudeEventType } from './types';

const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY;

/**
 * Campaign keys that are eligible for logging events to Amplitude
 */
const ELIGIBLE_CAMPAIGN_KEYS = [
  'GfwtpsL',
  'GfwtpsS',
  'g34bjedTT1',
  'g34bjeddBR1',
  'Maxim',
] as const;
const SEEN_EVENTS_TO_LOG_FOR_ALL_USERS = [
  'Upsale',
  'Paywall',
  'Succesful_payment',
] as const;

/** Campaigns that log onboarding_gender_selected */
const CAMPAIGNS_LOG_GENDER_SELECTION = [
  'g34bjedMG9_19_fm_G',
  'g34bjedMGrp_aio_G',
] as const;

/**
 * Amplitude module for logging events.
 */
class AmplitudeModule {
  private canLogEvents = false;
  private unsentEvents: AmplitudeEventType[] = [];
  private isInitialized = false;
  private readonly defaultTracking: amplitude.Types.AutocaptureOptions = {
    sessions: true,
    pageViews: false,
    formInteractions: false,
    fileDownloads: false,
  };

  /**
   * Initializes the Amplitude module with the given user ID.
   */
  init(userId: string): void {
    this.canLogEvents = this.isEligibleToLogEvents(userId);

    if (this.isInitialized) {
      console.warn('Amplitude already initialized, skipping re-initialization');
      return;
    }

    console.info('Amplitude init: eligible to log events');

    amplitude.add(new AmplitudePlatformPlugin());
    amplitude
      .init(AMPLITUDE_API_KEY, userId, {
        defaultTracking: this.defaultTracking,
      })
      .promise.then(() => {
        console.info('Amplitude initialized');
        this.isInitialized = true;
        this.flushUnsentEvents();
      })
      .catch((error) => {
        console.error('Amplitude initialization failed:', error);
      });
  }

  /**
   * Logs an event to Amplitude.
   * Tasks with business logic: https://pheon.atlassian.net/browse/PROD-2792
   */
  logEvent(
    eventName: AmplitudeEventType['eventName'],
    eventProperties: Record<string, unknown>,
  ): void {
    if (!this.isInitialized) {
      console.info(
        'Event is queued due to Amplitude NOT being initialized:',
        eventName,
        eventProperties,
      );
      this.unsentEvents.push({ eventName, eventProperties });
      return;
    }

    // Special case: Paywall events are always logged for all users
    if (
      eventName === 'onboarding_screen' &&
      SEEN_EVENTS_TO_LOG_FOR_ALL_USERS.includes(
        eventProperties.seen as (typeof SEEN_EVENTS_TO_LOG_FOR_ALL_USERS)[number],
      )
    ) {
      console.info('Logging event:', eventName, eventProperties);
      amplitude.track(eventName, eventProperties);
      return;
    }

    // Special case: Prepaid card declined events are always logged for all users
    if (eventName === 'prepaid_card_declined') {
      console.info(
        'Logging prepaid card declined event:',
        eventName,
        eventProperties,
      );
      amplitude.track(eventName, eventProperties);
      return;
    }

    // Campaigns that log gender selection
    if (
      eventName === 'onboarding_gender_selected' &&
      CAMPAIGNS_LOG_GENDER_SELECTION.includes(
        webCampaign.getKey() as (typeof CAMPAIGNS_LOG_GENDER_SELECTION)[number],
      )
    ) {
      amplitude.track(eventName, eventProperties);
      return;
    }

    // For eligible campaign keys, only 1/6 users can send events
    if (
      ELIGIBLE_CAMPAIGN_KEYS.includes(
        webCampaign.getKey() as (typeof ELIGIBLE_CAMPAIGN_KEYS)[number],
      )
    ) {
      if (this.canLogEvents) {
        console.info('Logging event:', eventName, eventProperties);
        amplitude.track(eventName, eventProperties);
      } else {
        console.warn(
          'Event is NOT logged due to id is not eligible to log events:',
          eventName,
          eventProperties,
        );
      }

      return;
    }

    console.warn(
      'Event is NOT logged due to eligibility criteria not met:',
      eventName,
      eventProperties,
    );
  }

  /**
   * Flushes unsent events that were queued before initialization.
   */
  private flushUnsentEvents(): void {
    console.log('Processing unsent events:', this.unsentEvents);

    this.unsentEvents.forEach(({ eventName, eventProperties }) => {
      this.logEvent(eventName, eventProperties);
    });

    this.unsentEvents = [];
  }

  /**
   * Determines if events can be logged based on the user's ID (UUID).
   * Allows logging only if the last character of the user ID, when converted to a decimal value,
   * is divisible by 6.
   * Tasks with business logic: https://pheon.atlassian.net/browse/PROD-2792
   */
  private isEligibleToLogEvents(userId: string): boolean {
    const numberInDecimal = retrieveDecimalDigitsFromUUID(userId, 2);

    if (numberInDecimal === null) {
      console.warn(
        'Invalid userId format. Cannot determine logging eligibility.',
      );
      return false;
    }

    return numberInDecimal % 6 === 0;
  }
}

export const amplitudeModule = new AmplitudeModule();
